import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, EventEmitter, inject, OnDestroy, signal, ViewChild, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { toError } from 'src/app/classes/shared/logs';
import { Pop } from 'src/app/classes/shared/pop';
import { UiTool } from 'src/app/classes/shared/ui.tools';
import { CumulativeDesign, defaultColors, DistributionDesign, GRAPH_TYPE_ALIASES, migrateChartDesign } from 'src/app/classes/widget/chartHandlerV2';
import { TemplateDialogComponent } from 'src/app/components/project-pages/dashboard/dialogs/template-dialog/template-dialog.component';
import { FieldPick, FieldPickerComponent, PickerGroup } from 'src/app/components/project-pages/dashboard/field-picker/field-picker.component';
import { FilterSummaryComponent } from 'src/app/components/project-pages/dashboard/filter-summary/filter-summary.component';
import { WidgetFormComponent } from 'src/app/components/project-pages/dashboard/widget-form/widget-form.component';
import { WidgetOptionCardComponent } from 'src/app/components/project-pages/dashboard/widget-option-card/widget-option-card.component';
import { WidgetGraphType, widgetGraphTypes } from 'src/app/components/project-pages/dashboard/widget-types';
import { HeatmapDesign } from 'src/app/components/project-pages/dashboard/widgets/heatmap/heatmap.component';
import { DEFAULT_RANKING_TOP, INSIGHT_NOTE_PRESETS, INSIGHT_NOTE_VARIABLES, InsightDesign } from 'src/app/components/project-pages/dashboard/widgets/insight/insight.component';
import { DEFAULT_MAX_RADIUS, ProjectMapDesign } from 'src/app/components/project-pages/dashboard/widgets/project-map/project-map.component';
import { TableColumnDesign, TableColumnFormat, tableColumns, tableOptionsEnabled } from 'src/app/components/project-pages/dashboard/widgets/table/table.component';
import { WidgetHostComponent } from 'src/app/components/project-pages/dashboard/widgets/widget-host/widget-host.component';
import { BY_PROJECT_WIDGET_TYPES, Chart, defaultForecast, FORECAST_BINNINGS, FORECAST_GRAPH_TYPES, PostChart, PROJECT_ROWS_WIDGET_TYPES, PutChart, templateBody, TemplateMeta, WidgetV2 } from 'src/app/interfaces/api/chart';
import { CompanyDashboard, WidgetEditorScope } from 'src/app/interfaces/api/dashboard';
import { Project } from 'src/app/interfaces/api/project';
import { Dashboard } from 'src/app/interfaces/shared/dashboard';
import { defaultChart } from 'src/app/interfaces/shared/defaultChart';
import { ChartService } from 'src/app/services/api/charts/chart.service';
import { DashboardService } from 'src/app/services/api/dashboards/dashboard.service';
import { ModelService } from 'src/app/services/api/models/model.service';
import { PlanningService } from 'src/app/services/api/plannings/planning.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { ZoneService } from 'src/app/services/api/zones/zone.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { DashboardStore } from 'src/app/services/stores/dashboard.store';
import { ButtonComponent, ColorPickerComponent, SpinnerComponent } from 'ui-library';
import { migrateBlockColors } from '../../widgets/text/block-colors';
import { LandingPeriod } from '../../widgets/text/landing';
import { AGGREGATE_INPUTS, DESIGN_INPUTS, FIELD_INPUTS, FILTER_INPUTS, SERIES_INPUTS } from '../../../../../interfaces/shared/defaultChart';
// import { OrderDataComponent } from '../dialog/order-data.component';

// The editor works on its own copy: the displayed widget stays as it is until the save, and the preview has its own refresh signal.
export function editableCopy(widget: WidgetV2): WidgetV2 {
    const clone = <T>(value: T): T => value === undefined ? value : JSON.parse(JSON.stringify(value));
    return { ...widget, query: clone(widget.query), design: clone(widget.design), dataset: clone(widget.dataset), signalSingled: signal('') };
}


// A design section with its own inputs: the editor writes their value, the shared definitions stay untouched.
function copyForm(form: any): any {
    return { ...form, options: form.options.map((o: any) => ({ ...o })) };
}

@Component({
    selector: 'app-widget-editor',
    templateUrl: './widget-editor.component.html',
    styleUrls: ['./widget-editor.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ColorPickerComponent,
        FieldPickerComponent,
        FilterSummaryComponent,
        WidgetHostComponent,
        MatCheckboxModule,
        FormsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatTooltipModule,
        MatSlideToggleModule,
        WidgetOptionCardComponent,
        TranslateModule,
        SpinnerComponent,
        MatIconModule,
        MatTabsModule,
        MatButtonModule,
        MatInputModule,
        WidgetFormComponent,
        ButtonComponent,
        MatMenuModule
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})

export class WidgetEditorComponent implements OnDestroy {
    // Services
    private readonly pop: Pop = inject(Pop);
    private readonly chartService: ChartService = inject(ChartService);
    private readonly auth: AuthService = inject(AuthService);
    private readonly tools: UiTool = inject(UiTool);
    private readonly projectService: ProjectService = inject(ProjectService);
    private readonly planningService: PlanningService = inject(PlanningService);
    private readonly modelService: ModelService = inject(ModelService);
    private readonly zoneService: ZoneService = inject(ZoneService);
    private readonly dashboardService: DashboardService = inject(DashboardService);
    private readonly translate: TranslateService = inject(TranslateService);
    private readonly dashboardStore: DashboardStore = inject(DashboardStore);
    private readonly dialog: MatDialog = inject(MatDialog);

    // Component API
    readonly data: any = inject(MAT_DIALOG_DATA);
    // Opened from a synthesis: tenant data, company dashboard. Absent: the project editor, unchanged.
    readonly scope: WidgetEditorScope | undefined = this.data.scope;
    // Dev/admin: save the widget as a catalog template, or edit a template opened from the catalog.
    readonly canManageTemplates: boolean = this.auth.isPrivileged();
    readonly editingTemplate: boolean = !!this.data.editingTemplate;
    // Synthesis: whether the previous widget type compared the projects (see syncByProject).
    private wasComparable?: boolean;

    readonly columnFormats: TableColumnFormat[] = ['text', 'chip', 'progress'];
    readonly defaultMaxRadius: number = DEFAULT_MAX_RADIUS;
    readonly defaultRankingTop: number = DEFAULT_RANKING_TOP;
    readonly insightSources: string[] = ['medianDays', 'meanDays', 'last', 'total', 'trend'];
    readonly forecastMethods: string[] = ['auto', 'holt', 'linear', 'poisson'];
    readonly landingPeriods: LandingPeriod[] = ['week', 'month', 'year'];
    readonly notePresets = INSIGHT_NOTE_PRESETS;
    readonly noteVariables = INSIGHT_NOTE_VARIABLES;
    readonly forecastConfidences: number[] = [0.8, 0.9, 0.95, 0.99];
    @ViewChild('titleSizeInput', { static: false }) titleSizeInput: ElementRef = new ElementRef(null);
    @ViewChild('valueSizeInput', { static: false }) valueSizeInput: ElementRef = new ElementRef(null);
    private dialogRef: MatDialogRef<WidgetEditorComponent> = inject(MatDialogRef<WidgetEditorComponent>);

    // Variables
    widget: WidgetV2 = {};
    chartQuery: any = {};
    chartDesignForm: any[] = [];
    title: string = '';
    maxDataValues: number[] = [1000, 500, 100, 50, 10];
    maxData: number = 1000;
    initialFlows: any;
    tabIndex: number = 0;
    showFieldList = false;
    destinationField: string = '';

    graphType: any;
    graphOption: any;
    compareTypes = ["Stacked Bar", "Grouped Bar", "Stacked Column", "Grouped Column", "Discrete Line", "Continious Line", "Discrete Area", "Continious Area", "Stacked Mix", "Grouped Mix", "Pie Chart", "Doughnut"]
    // Filtered by the context: a synthesis keeps the types that make sense over several projects.
    graphTypes: WidgetGraphType[] = widgetGraphTypes(!!this.scope);

    graphOptions: { [key: string]: { name: string, input: string, room: number, required: boolean }[] } = {
        type1: [
            { name: "X Axis", input: "field", room: 1, required: true },
            { name: "Y Axis", input: "aggregation", room: 3, required: true },
            { name: "Series", input: "series", room: 1, required: false },
        ],
        type2: [
            { name: "X Axis", input: "field", room: 1, required: true },
            { name: "Mix", input: "aggregation", room: 3, required: true },
        ],
        type3: [
            { name: "Value", input: "aggregation", room: 1, required: true },
        ],
        type4: [
            { name: "X Axis", input: "field", room: 1, required: true },
            { name: "Y Axis", input: "aggregation", room: 1, required: true },
            { name: "Intensity", input: "series", room: 1, required: false },
        ],
        type5: [
            { name: "Longitude", input: "longitude", room: 1, required: true },
            { name: "Latitude", input: "latitude", room: 1, required: true },
            { name: "Intensity", input: "series", room: 1, required: false },
        ],
        type6: [
            { name: "Label", input: "field", room: 1, required: true },
            { name: "Value", input: "aggregation", room: 1, required: true },
        ],
        type7: [
            { name: "Group", input: "field", room: 2, required: false },
            { name: "Value", input: "aggregation", room: 3, required: true },
        ],
        type8: [],
        type9: [
            { name: "Value", input: "aggregation", room: 1, required: false },
        ],
        // Distribution 100 %: one category per segment, no series.
        type10: [
            { name: "X Axis", input: "field", room: 1, required: true },
            { name: "Y Axis", input: "aggregation", room: 1, required: true },
        ],
        // Heatmap: a periodic date in columns, the series in rows in a project dashboard (the projects in a synthesis).
        type11: [
            { name: "X Axis", input: "field", room: 1, required: true },
            { name: "Value", input: "aggregation", room: 1, required: true },
            { name: "Series", input: "series", room: 1, required: false },
        ],
        // Project map: one value per project, no X axis (the filters still apply).
        type12: [
            { name: "Value", input: "aggregation", room: 1, required: true },
        ],
    }

    filterOptions: any = {
        type1: [
            { name: "X filter", input: "xFilter", room: 1 },
            { name: "Y filter", input: "yFilter", room: 1 },
        ]
    }

    theme = "light";
    projects: any[] = [];
    model: any;
    planning: any;
    zones: any[] = [];
    models: any[] = [];
    plannings: any[] = [];
    project?: Project;
    selectedProjects: string[] = [];
    searchText: string = '';
    user: any;
    @ViewChild('projectPaginator') projectPaginator: MatPaginator = new MatPaginator();
    @ViewChild(MatSort) projectSort: MatSort = new MatSort();
    projectsData: MatTableDataSource<any> = new MatTableDataSource();
    projectLoading: boolean = false;
    projectsCount: number = 0;
    projectsColumns: string[] = ['name', 'dashboard', 'add'];
    dashboard: any;
    // The fields of each source, loaded once while the editor is open (the picker fills it).
    fieldCache: Record<string, PickerGroup[]> = {};
    destroy$ = new Subject<void>();
    helpLink: string = "https://cad42.atlassian.net/wiki/x/AoDCK";
    n: number = 0;
    filterOption: any;
    compareDisplay: boolean = false;
    isAdd: boolean = false;
    tasks: any[] = [];
    query: any;
    dashboards: Dashboard[] = [];
    dashboardId: string = "";
    currentProject!: Project;
    isPersonal: boolean = false;

    // Signals
    refreshSignal: WritableSignal<number> = signal(0);
    visualSignal: WritableSignal<number> = signal(0);

    // Computed
    get widgetQueryLimit(): number {
        return this.widget?.query?.limit ?? 3600;
    }

    set widgetQueryLimit(value: number) {
        if (this.widget) {
            if (!this.widget.query) {
                this.widget.query = { filters: {}, aggregation: {}, limit: 3600 };
            }

            this.widget.query.limit = value;
        }
    }

    constructor() {
        this.user = this.auth.currentUser();
        // No current project in a synthesis: no model, zones, plannings nor project list to load.
        this.project = this.scope ? undefined : this.projectService.currentProjectValue;

        if (this.user) {
            this.theme = this.user.theme;
        }

        if (this.data.isPersonal)
            this.isPersonal = this.data.isPersonal;

        if (this.project) {
            if (!this.selectedProjects.length)
                this.selectedProjects = [this.project.id];

            this.getModel(this.project.model);
            this.getZones();
            this.getPlannings();
            this.getModels();
        }

        if (this.data.dashboard) {
            this.dashboard = this.data.dashboard;
        }

        if (this.data.widget) {
            this.widget = editableCopy(this.data.widget);

            // A removed type opens as the one that replaced it, and is saved as such.
            const alias = GRAPH_TYPE_ALIASES[this.widget.graphType ?? ''];
            if (alias) {
                this.widget.graphType = alias;
                delete this.widget.design?.limit;
            }

            this.widget.content = {};

            if (!this.widget.query) {
                this.widget.query = {
                    filters: {},
                    aggregation: {},
                    limit: 3600
                };
            } else {
                if (!this.widget.query.filters) {
                    this.widget.query.filters = {};
                }

                if (!this.widget.query.aggregation) {
                    this.widget.query.aggregation = {};
                }

                if (this.widget.query.limit === undefined || this.widget.query.limit === null) {
                    this.widget.query.limit = 3600;
                }
            }

            if (this.widget.design.planning3D) {
                this.getPlanningData(this.widget.design.planning3D.planning);
            }

            if (!this.scope && (!this.widget.projects || this.widget.projects.length == 0 || !this.isPersonal)) {
                this.selectedProjects = [this.project!.id];
                this.widget.projects = [this.project!.id];
            }

            if (!this.widget.dataset)
                this.widget.dataset = {};

            // The project editor builds the design inputs and the query options once its projects are loaded:
            // with no project list to wait for, a synthesis widget builds them right away.
            if (!this.scope) {
                this.getProjects(true);
            } else {
                this.onGraphTypeChange();
                this.updateWidget();
            }
        } else if (!this.scope) {
            this.getProjects(false);
        }

        if (Object.keys(this.widget).length === 0) {
            this.isAdd = true;
            this.widget = { ...defaultChart };

            // Its own query: syncByProject must not write into the shared defaultChart.
            if (this.scope)
                this.widget.query = { ...this.widget.query };

            this.widget.dataset = {};
            this.widget.design.datasetNames = [];

            // From the catalog: a blank widget of the chosen type.
            if (this.data.graphType)
                this.widget.graphType = this.data.graphType;

            this.onGraphTypeChange();

            // From the catalog: the chosen template, to customize.
            if (this.data.template)
                this.applyTemplate(this.data.template);
        }

        this.dashboards = this.dashboardStore.dashboards();
        this.dashboardId = this.dashboard.id;
    }

    ngOnDestroy(): void {
        this.widget = {};
        this.chartQuery = {};
        this.chartDesignForm = [];
        this.title = '';
        this.initialFlows = [];
        this.tabIndex = 0;
        this.showFieldList = false;
        this.destinationField = '';
        this.graphType = [];
        this.graphOption = null;
        this.filterOptions = null;
        this.projects = [];
        this.model = null;
        this.zones = [];
        this.models = [];
        this.planning = [];
        this.selectedProjects = [];

        this.destroy$.next();
        this.destroy$.complete();
    }

    updateWidget() {
        this.syncForecast();
        this.syncCompare();
        this.refreshSignal.set(this.refreshSignal() + 1);
    }

    updateVisuals(): void {
        this.visualSignal.set(this.visualSignal() + 1);
    }

    cleanEditor() {
        if (this.widget.query.field) this.widget.query.field = {};
        if (this.widget.query.aggregation) this.widget.query.aggregation = {};
        if (this.widget.query.filters) this.widget.query.filters = {};
        if (this.widget.query.xFilter) this.widget.query.xFilter = {};
        if (this.widget.query.yFilter) this.widget.query.yFilter = {};
    }

    onGraphTypeChange() {
        if (this.isAdd) {
            this.cleanEditor();
        }

        if (!this.widget.query) this.widget.query = {}

        this.graphType = this.graphTypes.find(g => g.value == this.widget.graphType);
        this.compareDisplay = this.compareTypes.includes(this.graphType?.name ?? '');
        const optionsKey = this.graphType?.options as keyof typeof this.graphOptions | undefined;
        this.graphOption = optionsKey && this.graphOptions[optionsKey] ? (this.graphOptions[optionsKey] as any).map((o: any) => { return { ...o } }) : []

        if (this.widget.compareMode && this.compareDisplay) {
            this.filterOption = this.filterOptions["type1"].map((o: any) => { return { ...o } })
            this.widget.query.filters = {};

            if (!this.widget.query.xFilter)
                this.widget.query.xFilter = {};

            if (!this.widget.query.yFilter)
                this.widget.query.yFilter = {};
        } else {
            this.filterOption = null;
            delete this.widget.query.yFilter;
            delete this.widget.query.xFilter;
        }

        Object.keys(this.widget.query).forEach((k: string) => {
            if (k != 'xFilter' && k != 'yFilter' && k != 'filters' && k != 'limit' && k != 'byProject' && k != 'forecast' && k != 'compare' && !this.graphOption.some((o: any) => o.input == k)) {
                this.widget.query[k] = {};
            }
        })

        this.widget.type = this.graphType.type;
        this.widget.graphOption = this.graphOption;

        // A heatmap without X starts on the hours of creation, rather than an empty grid.
        if (this.widget.type === 'heatmap' && !Object.keys(this.widget.query.field ?? {}).length) {
            const options = Object.fromEntries((FIELD_INPUTS as any).date.map((input: any) => [input.name, input.default]));
            this.widget.query.field = { createdDate: { type: 'date', options: { ...options, binning: 'hour', periodic: true } } };
        }

        if (this.zones && this.widget.content) {
            this.widget.content.zones = this.zones;
        }

        if (this.model && this.widget.content) {
            this.widget.content.model = this.model;
        }

        this.setFields();
        this.syncByProject();
        this.syncForecast();
        this.syncCompare();

        if (typeof this.widget.version === 'number') {
            this.widget.version++;
        } else {
            this.widget.version = 1;
        }
    }

    // Synthesis: compare the tenant projects by default where it reads well (charts, tables), a total elsewhere.
    // A widget turned from a total type (byProject forced to false) into a chart or a table compares again.
    syncByProject(): void {
        if (!this.scope || !this.widget.query) return;

        const comparable = this.canCompareProjects();

        if (PROJECT_ROWS_WIDGET_TYPES.includes(this.widget.type ?? ''))
            this.widget.query.byProject = true;
        else if (!comparable)
            this.widget.query.byProject = false;
        else if (this.isAdd || this.widget.query.byProject === undefined || this.wasComparable === false)
            this.widget.query.byProject = true;

        this.wasComparable = comparable;
    }


    canCompareProjects(): boolean {
        return BY_PROJECT_WIDGET_TYPES.includes(this.widget.type ?? '');
    }

    // Table: one display per column, in design.columns (text when absent).
    tableColumns(): string[] {
        return tableColumns(this.widget.query).columns;
    }

    // The column displays and the total row, behind the switch of their card.
    tableOptionsOn(): boolean {
        return tableOptionsEnabled(this.widget.design);
    }

    setTableOptions(enabled: boolean): void {
        this.widget.design.columnsEnabled = enabled;
        this.updateVisuals();
    }

    columnDesign(column: string): TableColumnDesign {
        return this.widget.design?.columns?.[column] ?? { format: 'text' };
    }

    setColumnFormat(column: string, format: TableColumnFormat): void {
        const columns = this.widget.design.columns ?? (this.widget.design.columns = {});

        if (format === 'text')
            delete columns[column];
        else
            columns[column] = { ...(format === 'chip' ? { higherIsWorse: true } : {}), ...columns[column], format };

        this.updateVisuals();
    }

    // Prediction: a line, area, column or bar over a date X axis binned by hour..year, not periodic.
    // In a project dashboard, several projects as one series each: the browser cannot merge their bands.
    canForecast(): boolean {
        return this.forecastBlockedReason() === null;
    }

    // Why the forecast is unavailable (i18n key), null when it is available.
    forecastBlockedReason(): string | null {
        if (!FORECAST_GRAPH_TYPES.includes(this.widget.graphType ?? ''))
            return 'editor.forecast_needs_graph_type';

        if (!this.scope && (this.widget.projects?.length ?? 0) > 1 && !this.widget.databaseSeries)
            return 'editor.forecast_needs_one_project';

        return this.hasDateAxis() ? null : 'editor.forecast_needs_date_axis';
    }

    // An X axis of dates grouped by hour..year, not periodic: what a forecast or a running total runs along.
    hasDateAxis(): boolean {
        const fields = this.widget.query?.['x-axis'] || this.widget.query?.field || {};
        const first = fields[Object.keys(fields)[0] ?? ''];
        return first?.type === 'date' && FORECAST_BINNINGS.includes(first.options?.binning) && !first.options?.periodic;
    }

    // Running total and its target, in design.cumulative (created with the card).
    get cumulative(): CumulativeDesign {
        return this.widget.design.cumulative ?? (this.widget.design.cumulative = {});
    }

    // Settings of the distribution, in design.distribution (created with the card).
    get distribution(): DistributionDesign {
        return this.widget.design.distribution ?? (this.widget.design.distribution = {});
    }

    // Heatmap columns: a periodic date (hours, weekdays, months) on X.
    heatmapBlockedReason(): string | null {
        const fields = this.widget.query?.field ?? {};
        const first = fields[Object.keys(fields)[0] ?? ''];
        const periodic = first?.type === 'date' && first.options?.periodic && ['hour', 'day', 'month'].includes(first.options?.binning);
        return periodic ? null : 'editor.heatmap_needs_periodic_date';
    }

    get projectMap(): ProjectMapDesign {
        return this.widget.design.projectMap ?? (this.widget.design.projectMap = {});
    }

    // Insight blocks, in design.insight; each one created on its first switch.
    insightBlock<K extends keyof InsightDesign>(block: K): NonNullable<InsightDesign[K]> {
        const insight = this.widget.design.insight ?? (this.widget.design.insight = {});
        return insight[block] ?? (insight[block] = {} as NonNullable<InsightDesign[K]>);
    }

    // A ready-made note, in the language of the editor: the user rewrites it as wanted.
    applyNotePreset(preset: string): void {
        this.insightBlock('note').text = this.translate.instant('editor.insight_note_preset_' + preset + '_text');
        this.updateVisuals();
    }

    // A variable added at the end of the note.
    addNoteVariable(variable: string): void {
        const note = this.insightBlock('note');
        note.text = `${note.text ?? ''}${note.text && !note.text.endsWith(' ') ? ' ' : ''}{${variable}}`;
        this.updateVisuals();
    }

    toggleInsightBlock(block: keyof InsightDesign, enabled: boolean): void {
        this.insightBlock(block).enabled = enabled;
        this.updateVisuals();
    }

    get heatmap(): HeatmapDesign {
        return this.widget.design.heatmap ?? (this.widget.design.heatmap = {});
    }

    // The optional cards go right after the title settings (the "text" card of a block).
    isTitleForm(form: any): boolean {
        return form?.name === (this.widget.type === 'block' ? 'text' : 'title');
    }

    hasTitleForm(): boolean {
        return this.chartDesignForm.some(form => this.isTitleForm(form));
    }

    get forecastEnabled(): boolean {
        return !!this.widget.query?.forecast?.enabled;
    }

    set forecastEnabled(enabled: boolean) {
        if (enabled)
            this.widget.query.forecast = { ...defaultForecast };
        else
            delete this.widget.query.forecast;

        this.updateWidget();
    }

    onForecastHorizon(): void {
        const forecast = this.widget.query.forecast;
        forecast.horizonDays = Math.min(this.forecastMaxHorizon(), Math.max(1, Math.round(Number(forecast.horizonDays) || defaultForecast.horizonDays)));
        this.updateWidget();
    }

    // The furthest the API projects: 14 days by the hour, 2 years otherwise.
    forecastMaxHorizon(): number {
        const fields = this.widget.query?.['x-axis'] || this.widget.query?.field || {};
        return fields[Object.keys(fields)[0] ?? '']?.options?.binning === 'hour' ? 14 : 730;
    }

    // Poisson models a number of events: a count only.
    forecastCounts(): boolean {
        return this.aggregateOf() === 'count';
    }

    // A landing adds up what is done and what is to come: a count or a sum.
    forecastAdditive(): boolean {
        return ['count', 'sum'].includes(this.aggregateOf() ?? '');
    }

    private aggregateOf(): string | undefined {
        const aggregation = this.widget.query?.aggregation || {};
        return aggregation[Object.keys(aggregation)[0] ?? '']?.options?.aggregate;
    }

    // Its own query (a daily series and its forecast): switching it queries again.
    toggleLanding(enabled: boolean): void {
        this.widget.design.landing = { period: 'month', ...this.widget.design.landing, enabled };
        this.updateWidget();
    }

    // KPI (text block): compared to the previous period, which the API derives from a date filter.
    hasDateFilter(): boolean {
        return Object.values(this.widget.query?.filters ?? {}).some((filter: any) => filter?.type === 'date');
    }

    get compareEnabled(): boolean {
        return !!this.widget.query?.compare?.previous;
    }

    set compareEnabled(enabled: boolean) {
        if (enabled)
            this.widget.query.compare = { previous: true };
        else
            delete this.widget.query.compare;

        this.updateWidget();
    }

    get trendHigherIsWorse(): boolean {
        return !!this.widget.design?.trend?.higherIsWorse;
    }

    set trendHigherIsWorse(higherIsWorse: boolean) {
        this.widget.design.trend = { ...this.widget.design.trend, higherIsWorse };
        this.updateVisuals();
    }

    private syncCompare(): void {
        if (this.widget.query?.compare && (this.widget.type !== 'block' || !this.hasDateFilter())) delete this.widget.query.compare;
    }

    // The option leaves the query as soon as the type, the X axis or the projects no longer allow it.
    private syncForecast(): void {
        if (this.widget.query?.forecast && !this.canForecast()) delete this.widget.query.forecast;
    }

    setFields() {
        try {
            let config = this.widget;
            let field: any[] = [], longitude: any[] = [], latitude: any[] = [], aggregation: any[] = [], series: any[] = [], filters: any[] = [], xFilter: any[] = [], yFilter: any[] = [], link: any[] = [];

            if (config.query && config.query.link) {

                let form: any = FIELD_INPUTS["link"].map((i: any) => { return { ...i } })

                form.forEach((input: any) => {
                    let value = config.query.link.options[input.name];

                    if (value !== undefined) {
                        input.value = value;
                    }
                    return input;
                })

                link = [{
                    name: "editor.link",
                    type: "link",
                    options: form
                }]
            }

            if (config.query && config.query.longitude)
                longitude = Object.keys(config.query.longitude).map((k: string) => {
                    let type = config.query.longitude[k].type;
                    let form: any = (SERIES_INPUTS as any)[type].map((i: any) => { return { ...i } })

                    form.forEach((input: any) => {
                        let value = config.query.longitude[k].options[input.name];

                        if (value !== undefined) {
                            input.value = value;
                        }

                        return input;
                    })

                    return {
                        name: k,
                        type,
                        options: form
                    }
                })

            if (config.query && config.query.latitude)
                latitude = Object.keys(config.query.latitude).map((k: string) => {
                    let type = config.query.latitude[k].type;
                    let form: any = (SERIES_INPUTS as any)[type].map((i: any) => { return { ...i } })
                    // let options
                    form.forEach((input: any) => {
                        let value = config.query.latitude[k].options[input.name];

                        if (value !== undefined) {
                            input.value = value;
                        }
                        return input;
                    })
                    return {
                        name: k,
                        type,
                        options: form
                    }
                })

            if (config.query && config.query.field) {
                field = Object.keys(config.query.field).map((k: string) => {
                    let type = config.query.field[k].type;
                    let form: any = (FIELD_INPUTS as any)[type].map((i: any) => { return { ...i } })
                    // let options
                    form.forEach((input: any) => {
                        let value = config.query.field[k].options[input.name];

                        if (value !== undefined) {
                            input.value = value;
                        }
                        return input;
                    })
                    return {
                        name: k,
                        type,
                        options: form
                    }
                })

                if (config.query && config.query.xFilter) {
                    xFilter = Object.keys(config.query.xFilter).map((k: string) => {
                        let type = config.query.xFilter[k].type;
                        let form: any = (FILTER_INPUTS as any)[type].map((i: any) => { return { ...i } })
                        // let options
                        form.forEach((input: any) => {
                            let value = config.query.xFilter[k].options[input.name];

                            if (value !== undefined) {
                                input.value = value;
                            }
                            return input;
                        })
                        return {
                            name: k,
                            type,
                            options: form
                        }
                    })
                }

                if (config.query && config.query.yFilter) {
                    yFilter = Object.keys(config.query.yFilter).map((k: string) => {
                        let type = config.query.yFilter[k].type;
                        let form: any = (FILTER_INPUTS as any)[type].map((i: any) => { return { ...i } })

                        form.forEach((input: any) => {
                            let value = config.query.yFilter[k].options[input.name];

                            if (value !== undefined) {
                                input.value = value;
                            }
                            return input;
                        })

                        return {
                            name: k,
                            type,
                            options: form
                        }
                    })
                }
            }

            let aggretionTypes: any[] = [];

            if (config.query && config.query.aggregation)
                aggregation = Object.keys(config.query.aggregation).map((k: string) => {
                    let type = config.query.aggregation[k].type;
                    aggretionTypes.push(type)
                    let form: any = (AGGREGATE_INPUTS as any)[type].map((i: any) => { return { ...i } });

                    if (this.graphType?.type != "block") {
                        form.forEach((input: any) => {
                            if (!input.options) return;
                            input.options = input.options.filter((opt: any) => opt != "enumerate")
                        })
                    }

                    form.forEach((input: any) => {
                        if (!config.query.aggregation[k].options) return;
                        let value = config.query.aggregation[k].options[input.name];

                        if (value !== undefined) {
                            input.value = value;
                        }
                    })

                    return {
                        name: k,
                        type,
                        options: form
                    }
                })

            if (!this.widget.databaseSeries && config.query && config.query.series) {
                series = Object.keys(config.query.series).map((k: string) => {
                    let type = config.query.series[k].type;
                    let form: any = (SERIES_INPUTS as any)[type].map((i: any) => { return { ...i } });

                    form.forEach((input: any) => {
                        let value = config.query.series[k].options[input.name];
                        if (value !== undefined) {
                            input.value = value;
                        }
                    })

                    return {
                        name: k,
                        type,
                        options: form
                    }
                })
            } else if (this.widget.databaseSeries) {
                series = [{
                    name: "Project Series",
                    type: "string",
                    options: []
                }]
            }

            if (config.query && config.query.filters)
                filters = Object.keys(config.query.filters).map((k: string) => {
                    let type = config.query.filters[k].type;
                    let form: any = (FILTER_INPUTS as any)[type].map((i: any) => { return { ...i } });

                    form.forEach((input: any) => {
                        let value = config.query.filters[k].options[input.name];
                        if (value) {
                            input.value = value;
                        }
                    })

                    return {
                        name: k,
                        type,
                        options: form
                    }
                })

            if (config.design && config.type) {
                // Sections saved before their switch: on as they were drawn.
                if (config.type == 'chart' || config.type == 'insight') migrateChartDesign(config.design);

                // A new block with its accent bar; a saved one with the theme colors instead of the former defaults.
                if (config.type == 'block') {
                    if (this.isAdd) {
                        config.design.colors ??= {};
                        config.design.colors.accent ??= true;
                    }
                    migrateBlockColors(config.design);
                }

                this.chartDesignForm = (DESIGN_INPUTS as any)[config.type].map(copyForm);

                // A chart or an insight: the format of its Y axis.
                if (this.widget.type == 'chart' || this.widget.type == 'insight') {
                    if (config.design.yAxis && config.design.yAxis.format) {
                        let format: any = (DESIGN_INPUTS as any)['format'].find((f: any) => f.name == config.design.yAxis.format + "Format")
                        this.chartDesignForm.push(copyForm(format));
                    } else {
                        this.chartDesignForm.push(copyForm((DESIGN_INPUTS as any)['format'][0]));
                    }
                }
                if (this.widget.type == 'block') {
                    if (config.design.text && config.design.text.format) {
                        let format: any = (DESIGN_INPUTS as any)['format'].find((f: any) => f.name == config.design.text.format + "Format")
                        this.chartDesignForm.push(copyForm(format));
                    } else {
                        this.chartDesignForm.push(copyForm((DESIGN_INPUTS as any)['format'][0]));
                    }
                }

                if (this.widget.type == 'table') {
                    this.chartDesignForm.find((design: any) => design.name == 'yFormat').options = []

                    Object.keys(config.query.aggregation).forEach((key: string, index: number) => {
                        const keyY = 'Y' + (index + 1);

                        let yFormatInput: any = { ...(DESIGN_INPUTS as any)['table'].find((inp: any) => inp.name == 'yFormat')?.options[0] };
                        yFormatInput.label = keyY;
                        yFormatInput.name = keyY;

                        if ((this.chartDesignForm as any)["yFormat"]) {
                            yFormatInput.default = (this.chartDesignForm as any)["yFormat"][index];
                        }

                        this.chartDesignForm.find((design: any) => design.name == 'yFormat').options.push({ ...yFormatInput });

                        let format: any = { ...(DESIGN_INPUTS as any)['format'][0] };

                        if (config.design["yFormat"] && config.design["yFormat"][keyY])
                            format = { ...(DESIGN_INPUTS as any)['format'].find((f: any) => f.name == config.design["yFormat"][keyY] + "Format") };

                        format.label = keyY;
                        format.name = keyY;

                        this.chartDesignForm.push(copyForm(format));
                    });
                }

                if (this.widget.type == 'planning3D') {
                    if (this.widget.design.planning3D?.planning) {
                    }
                }

                this.chartDesignForm.forEach((form: any) => {
                    form.options.forEach((o: any) => {
                        if (o.type == "colors") {
                            // The section exists with its switch alone until a colour is picked.
                            const colorList = config.design["colors"]?.colorList;
                            if (!colorList || Object.keys(colorList).length == 0) {
                                o.options = defaultColors.map((c, i) => {
                                    return {
                                        label: i,
                                        value: c
                                    }
                                })

                            } else {
                                o.options = Object.keys(colorList).map(k => {
                                    return { label: k, value: colorList[k] }
                                });
                            }
                        }

                        if (o.name == "planning") {
                            o.options = this.plannings.map(z => {
                                return {
                                    name: z.name,
                                    value: z.id
                                }
                            })
                        }

                        if (o.name == "tasks") {
                            o.options = this.tasks.map(z => {
                                return {
                                    name: z.number + " " + z.text,
                                    value: z.id
                                }
                            })
                        }
                        if (o.name == "modelList") {
                            o.options = this.models.map(z => {
                                return {
                                    name: z.name,
                                    value: z.name
                                }
                            })
                        }

                        if (o.name == "zoneList") {
                            o.options = this.zones.map(z => {
                                return {
                                    name: z.name,
                                    value: z.name
                                }
                            })
                        }
                        // Saved: shown as it is; missing: its default, saved with the widget (the chart reads it).
                        const section = config.design[form.name] ?? (config.design[form.name] = {});
                        if (section[o.name] !== undefined && !this.isAdd) {
                            o.value = section[o.name];
                        } else if (section[o.name] === undefined && o.default !== undefined && typeof o.default !== 'object') {
                            section[o.name] = o.default;
                        }
                    })
                })
            }

            this.chartQuery = {
                field: field,
                longitude: longitude,
                latitude: latitude,
                aggregation: aggregation,
                series: series,
                filters: filters,
                xFilter: xFilter,
                yFilter: yFilter
            }
        } catch (e) {
            console.error(e);
        }

    }

    setFormatFields(name: string) {
        let config = this.widget;
        let formIndex = this.chartDesignForm.findIndex((form: any) => form.name == name);
        let formatChanged = false;
        if (config.design && formIndex >= 0 && config.type) {
            let designInputForm: any = (DESIGN_INPUTS as any)[config.type]?.find((form: any) => form.name == this.chartDesignForm[formIndex].name)
            if (["chart", "insight", "block"].includes(this.widget.type || '') && designInputForm) {
                let formatIndex = this.chartDesignForm.findIndex((form: any) => /format/i.test(form.name));
                let designFormatKey = config.type == "block" ? "text" : "yAxis";
                let formatForm: any = config.design[designFormatKey]?.format ? (DESIGN_INPUTS as any)['format'].find((f: any) => f.name == config.design[designFormatKey].format + "Format") : (DESIGN_INPUTS as any)["format"][0];
                if (formatIndex >= 0 && formatForm?.name != this.chartDesignForm[formatIndex].name) {
                    formatChanged = true;
                    this.chartDesignForm[formIndex] = copyForm(designInputForm);
                    this.chartDesignForm.splice(formatIndex, 1);
                    this.chartDesignForm.push(copyForm(formatForm));
                }
            }

            if (formatChanged) {
                this.chartDesignForm.forEach((form: any) => {
                    if (form.name != name && !/format/i.test(form.name)) return;
                    form.options.forEach((o: any) => {
                        if (config.design[form.name] && config.design[form.name][o.name] != undefined) {
                            o.value = config.design[form.name][o.name];
                        } else {
                            if (!config.design[form.name]) {
                                config.design[form.name] = {};
                                config.design[form.name][o.name] = o.default;
                            }
                        }
                    })
                })
            }
        }
    }

    onFormUpdate(event: any) {
        if (event.remove) {
            if (this.hasKeyValue(event.form.name))
                this.removeKeyValue(event.form.name);

            delete this.widget.query[event.field][event.form.name];
            this.setAggregationVisibility(event.field);
            this.setFields();
            this.updateWidget();
            return;
        }

        if (this.widget.type == "video") {
            this.updateWidget();
        }

        if (event.field !== "design" && event.typeChanged) {
            this.widget.query[event.field][event.form.name].type = event.form.type;
            this.setAggregationVisibility(event.field);
            this.setFields();
            this.updateWidget();
            return;
        }

        let options: any = {};
        event.inputs.forEach((i: any) => {
            if (i.name == "colorList") {
                options[i.name] = {}
                i.options.forEach((o: any) => {
                    options[i.name][o.label] = o.value;
                })
            } else {
                options[i.name] = i.value;
            }
        })

        if (event.field !== "design") {
            this.widget.query[event.field][event.form.name] = {
                type: event.form.type,
                options
            }

            if (options["dataset_name"] != undefined) {
                if (!this.widget.design.datasetNames)
                    this.widget.design.datasetNames = [];
                if (options["dataset_name"])
                    this.setKeyValue(event.form.name, options["dataset_name"]);
                else this.removeKeyValue(event.form.name);
                this.widget = { ...this.widget };
            }

            this.updateWidget();
        } else {
            this.widget.design[event.form.name] = options;

            if (options.format)
                this.setFormatFields(event.form.name);
            else if (options["Y1"])
                this.setFields();
            else if (event.form.name == "planning3D" && options["planning"])
                this.getPlanningData(options["planning"]);

            this.updateVisuals();
        }
    }

    hasKeyValue(key: any, arr: any[] | null = null): boolean {
        try {
            arr = arr ?? this.widget.design.datasetNames;
            return Boolean(arr && arr.findIndex((data: any) => data.key == key) >= 0);
        } catch (err) {
            console.error("Error while checking if key-value pair exists :>>", err);
            return false;
        }
    }

    setKeyValue(key: any, value: any, arr: any[] | null = null) {
        try {
            arr = arr ?? this.widget.design.datasetNames;
            if (!arr) return;
            let dataIndex = arr.findIndex((data: any) => data.key == key);
            if (dataIndex >= 0)
                arr[dataIndex].value = value;
            else
                arr.push({ key, value });
        } catch (err) {
            console.error("Error while setting key-value pair :>>", err);
        }
    }

    getKeyValue(key: any, arr: any[] | null = null): any {
        try {
            arr = arr ?? this.widget.design.datasetNames;
            if (!arr) return undefined;
            let dataIndex = arr.findIndex((data: any) => data.key == key);
            if (dataIndex >= 0)
                return arr[dataIndex].value;
            return undefined;
        } catch (err) {
            console.error("Error while getting key-value pair :>>", err);
            return undefined;
        }
    }

    removeKeyValue(key: any, arr: any[] | null = null): any {
        try {
            arr = arr ?? this.widget.design.datasetNames;
            if (!arr) return undefined;
            let dataIndex = arr.findIndex((data: any) => data.key == key);
            if (dataIndex >= 0)
                return arr.splice(dataIndex, 1);
            return undefined;
        } catch (err) {
            console.error("Error while removing key-value pair :>>", err);
            return undefined;
        }
    }

    addField(axis: any) {
        this.destinationField = axis;
        this.showFieldList = true;
    }

    addFilter() {
        this.destinationField = "filters";
        this.showFieldList = true;
    }

    // The source of the fields already chosen: an aggregation reads one collection, the others are locked.
    lockedSource(): string | null {
        const query = this.widget?.query ?? {};
        const used = ['field', 'aggregation', 'series', 'filters', 'longitude', 'latitude']
            .some(key => query[key] && typeof query[key] === 'object' && Object.keys(query[key]).length);
        return used ? (this.widget.category || 'metric') : null;
    }

    // A field of the picker: its source becomes the widget one; a field of one measure type keeps that type only.
    pickField(pick: FieldPick): void {
        this.widget.category = pick.collection;
        if (pick.metricNames && this.destinationField !== 'filters') this.keepMetricNames(pick.metricNames);

        const dot = pick.path.indexOf('.');
        if (dot < 0) this.selectField({ name: pick.path, type: pick.type }, null);
        else this.selectField({ name: pick.path.slice(0, dot) }, { name: pick.path.slice(dot + 1), type: pick.type });
    }

    // The filter on the measure type (its names: Séquence and Séquences), added or widened: Coulage volumes, not every volume.
    private keepMetricNames(names: string[]): void {
        const filters = this.widget.query.filters ?? (this.widget.query.filters = {});
        const current = filters['name'];
        if (current?.options?.use === 'list' && Array.isArray(current.options.values)) {
            current.options.values = [...new Set([...current.options.values, ...names])];
            return;
        }
        if (current) return;

        const options: any = {};
        (FILTER_INPUTS as any).string.forEach((input: any) => options[input.name] = input.default);
        filters['name'] = { type: 'string', options: { ...options, use: 'list', values: [...names] } };
    }

    selectField(field: any, option: any) {
        this.closeFieldList();
        let currentField: any;
        if (option) {
            currentField = {
                name: field.name + "." + option.name,
                type: option.type.toLowerCase()
            }
        } else {
            currentField = {
                name: field.name,
                type: field.type.toLowerCase()
            }
        }

        let options: any = {};
        if (this.destinationField == 'field') {
            let inputs: any = (FIELD_INPUTS as any)[currentField.type].map((i: any) => { return { ...i } });
            inputs.forEach((i: any) => {
                options[i.name] = i.default;
            })
        } else if (this.destinationField == 'aggregation') {
            let inputs: any = (AGGREGATE_INPUTS as any)[currentField.type].map((i: any) => { return { ...i } });

            if (this.graphType.type != "block") {
                inputs.forEach((input: any) => {
                    if (!input.options) return;
                    input.options = input.options.filter((opt: any) => opt != "enumerate")
                })
            }
            inputs.forEach((i: any) => {
                options[i.name] = i.default;
            })
        } else if (this.destinationField == 'series' || this.destinationField == 'longitude') {
            let inputs: any = (SERIES_INPUTS as any)[currentField.type].map((i: any) => { return { ...i } });
            inputs.forEach((i: any) => {
                options[i.name] = i.default;
            })
        } else if (this.destinationField == 'xFilter' || this.destinationField == 'yFilter') {
            let inputs: any = (FILTER_INPUTS as any)[currentField.type].map((i: any) => { return { ...i } });
            inputs.forEach((i: any) => {
                options[i.name] = i.default;
            })

        } else if (this.destinationField == 'filters') {
            let inputs: any = (FILTER_INPUTS as any)[currentField.type].map((i: any) => { return { ...i } });
            inputs.forEach((i: any) => {
                options[i.name] = i.default;
            })
        }
        if (!this.widget.query[this.destinationField]) this.widget.query[this.destinationField] = {};

        this.widget.query[this.destinationField][currentField.name] = {
            type: currentField.type,
            options
        }

        //clean up    
        this.setAggregationVisibility(this.destinationField);

        this.setFields();
        this.updateWidget();
    }


    closeFieldList(): void {
        this.showFieldList = false;
    }

    setAggregationVisibility(destinationField: any) {
        if (destinationField == 'series') {
            let aggreKeys: any[] = [];
            if (this.widget.query.aggregation)
                aggreKeys = Object.keys(this.widget.query.aggregation);
            if (aggreKeys.length > 1) {
                aggreKeys.forEach((k: string, i: number) => {
                    if (i > 0) {
                        delete this.widget.query.aggregation[k]
                    }
                })
            }

            if (this.widget.databaseSeries || (this.widget.query.series && Object.keys(this.widget.query.series).length)) {
                this.graphOption[1].room = 1;
            } else {
                this.graphOption = (this.graphOptions as any)[this.graphType.options]?.map((o: any) => { return { ...o } })
            }
        }

        if (destinationField == 'aggregation') {
            let aggreKeys = Object.keys(this.widget.query.aggregation);
            if (aggreKeys.length > 1 && this.widget.query.series) {
                delete this.widget.query.series

                this.graphOption[2].room = 0;
            } else {
                this.graphOption = (this.graphOptions as any)[this.graphType.options]?.map((o: any) => { return { ...o } })
            }
        }
    }

    onMaxDataChange(value: any) {
        this.maxData = value;
    }

    onTabChanged(event: any) {
        this.tabIndex = event.index
    }

    saveChart() {
        if (this.editingTemplate) return this.saveTemplate();
        if (this.scope) return this.saveCompanyChart(this.scope);

        try {
            let chart = { ...this.widget };
            let newDashboard = this.dashboardId != this.data?.dashboard.id ? this.dashboards?.find((d: Dashboard) => d.id == this.dashboardId) : null;

            delete chart.data;
            delete chart.refresh;
            delete chart.export;
            chart.template = false;

            if (this.data.dashboard && this.data.dashboard.owner) {
                chart.destination = "user"
            } else {
                chart.destination = "project"
            }

            if (chart.type !== "map" && chart.type != "forge" && chart.type != "planning3D") {
                chart.content = {}
            }

            if (newDashboard) {
                chart.index = this.widget.index = "";
            }

            if (chart.id) {
                this.chartService.updateChart(chart as PutChart).pipe(takeUntil(this.destroy$)).subscribe(async res => {
                    if (!newDashboard && this.data.dashboard) {
                        this.pop.success(this.translate.instant('editor.chart_saved'))
                        this.dialogRef.close({ widget: this.savedWidget(), dashboard: this.data.dashboard, newDashboard });
                        return;
                    }

                    if (this.data.dashboard && newDashboard) {
                        await this.updateDashboard({ dashboard: this.data.dashboard, widget: this.widget, action: "remove", pop: false });
                        await this.updateDashboard({ dashboard: newDashboard, widget: this.widget });
                    }

                    this.dialogRef.close({ widget: this.widget, dashboard: this.data.dashboard, newDashboard });
                })
            } else {
                delete chart.createdDate;
                chart.user = this.user.id;
                chart.index = this.widget.index = "";

                this.chartService.addChart(chart as PostChart).pipe(takeUntil(this.destroy$)).subscribe(async (res: Chart) => {
                    if (res.id) {
                        this.widget.id = res.id

                        if (!newDashboard && this.data.dashboard)
                            await this.updateDashboard({ dashboard: this.data.dashboard, widget: this.widget });
                        else if (newDashboard) {
                            await this.updateDashboard({ dashboard: newDashboard, widget: this.widget });
                        }
                        this.dialogRef.close({ widget: this.widget, dashboard: this.data.dashboard, newDashboard });

                    } else {
                        this.pop.success(this.translate.instant('editor.chart_saved'))
                        this.dialogRef.close({ widget: this.widget, dashboard: this.data.dashboard });
                    }
                })
            }

        } catch (e) {
            console.error(e)
        }
    }

    // The saved settings on a new object, with the data and refresh signal of the displayed widget, in its place in the
    // dashboard: its view takes the new settings at once (new input), then queries its data again.
    private savedWidget(): WidgetV2 {
        const displayed: WidgetV2 | undefined = this.data.widget;
        const saved: WidgetV2 = { ...this.widget, data: displayed?.data, signalSingled: displayed?.signalSingled };

        const charts: any[] | undefined = this.data.dashboard?.charts;
        const index = charts?.findIndex(chart => chart?.id === saved.id) ?? -1;
        if (charts && index >= 0) charts[index] = saved;

        saved.signalSingled?.set(`refresh_manuel_${Date.now()}`);
        return saved;
    }

    // The current configuration as a new catalog template; the widget itself is left unchanged.
    saveAsTemplate(): void {
        this.openTemplateDialog({ title: this.widget.title }).subscribe(meta => {
            if (!meta) return;

            this.chartService.addTemplate(templateBody(this.widget, meta)).pipe(takeUntil(this.destroy$)).subscribe({
                next: () => this.pop.success(this.translate.instant('catalog.template_saved')),
                error: (err: unknown) => this.pop.error(toError(err))
            });
        });
    }

    // A template opened from the catalog: its title, theme and description, then its update.
    private saveTemplate(): void {
        const template: any = this.widget;

        this.openTemplateDialog({ title: template.title, theme: template.theme, description: template.description, editing: true }).subscribe(meta => {
            if (!meta) return;

            this.chartService.updateTemplate(template.id, templateBody(this.widget, meta)).pipe(takeUntil(this.destroy$)).subscribe({
                next: (updated: WidgetV2) => {
                    this.pop.success(this.translate.instant('catalog.template_saved'));
                    this.dialogRef.close({ template: updated });
                },
                error: (err: unknown) => this.pop.error(toError(err))
            });
        });
    }

    private openTemplateDialog(data: Partial<TemplateMeta> & { editing?: boolean }): Observable<TemplateMeta | undefined> {
        return this.dialog.open(TemplateDialogComponent, { width: '480px', maxWidth: '95vw', data }).afterClosed();
    }

    // Synthesis widget: created then added to the company dashboard, which the API sends back whole.
    private saveCompanyChart(scope: WidgetEditorScope): void {
        const chart: any = { ...this.widget, destination: 'company', company: scope.companyId, projects: [], template: false };
        delete chart.data;
        delete chart.refresh;
        delete chart.export;

        if (chart.type !== "map")
            chart.content = {};

        const onError = (err: unknown) => this.pop.error(toError(err));

        if (chart.id) {
            this.chartService.updateChart(chart as PutChart).pipe(takeUntil(this.destroy$)).subscribe({
                next: () => {
                    this.pop.success(this.translate.instant('editor.chart_saved'));
                    this.dialogRef.close({ widget: this.savedWidget() });
                },
                error: onError
            });
            return;
        }

        delete chart.createdDate;
        chart.user = this.user.id;
        chart.index = "";

        this.chartService.addChart(chart as PostChart).pipe(takeUntil(this.destroy$)).subscribe({
            next: (created: Chart) => this.dashboardService.addCompanyChart(scope.companyId, scope.theme, created.id)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (companyDashboard: CompanyDashboard) => {
                        this.pop.success(this.translate.instant('editor.chart_saved'));
                        this.dialogRef.close({ companyDashboard });
                    },
                    error: onError
                }),
            error: onError
        });
    }

    updateDashboard(args: { dashboard: Dashboard, widget: WidgetV2, action?: 'add' | 'remove', pop?: boolean }) {
        let { dashboard, widget, action = "add", pop = true } = args;
        return new Promise((resolve, reject) => {
            const chartIds = dashboard.charts?.map((c: WidgetV2 | string) => typeof c == 'string' ? c : c.id).filter(id => id) || [];

            if (action == "add" && !chartIds.includes(widget.id)) {
                dashboard.charts?.push(widget);
            }

            if (action == 'remove' && chartIds.includes(widget.id)) {
                dashboard.charts = dashboard.charts?.filter(c => (c as WidgetV2).id != widget.id);
            }

            // $addToSet/$pull côté serveur au lieu d'envoyer le tableau `charts`
            // complet recalculé ici : deux ajouts/retraits concurrents sur le même
            // dashboard ne s'écrasent plus jamais, peu importe l'ordre d'arrivée.
            const persist$ = action === 'add'
                ? this.dashboardService.addChartToDashboard(dashboard.id, widget.id!)
                : this.dashboardService.removeChartFromDashboard(dashboard.id, widget.id!);

            persist$.pipe(takeUntil(this.destroy$)).subscribe({
                next: () => {
                    if (pop)
                        this.pop.success(this.translate.instant('editor.chart_saved'));
                    resolve("Ok");
                },
                error: (err) => {
                    this.pop.error(toError(err));
                    reject(toError(err));
                }
            });
        });
    }

    parseProjects() {
        this.projects.map(p => {
            if (p.name.length > 20) {
                p.nameShort = p.name.slice(0, 17).trim() + '...'
            } else {
                p.nameShort = p.name;
            }

            p.selected = this.selectedProjects.includes(p.id);
        });

        this.projects.sort((a, b) => a.name < b.name ? -1 : 1);
        this.projects.sort((a, b) => (a.selected === b.selected) ? 0 : a.selected ? -1 : 1);
        this.projects.sort((a, b) => a.archived == b.archived ? 0 : a.archived ? 1 : -1);
        this.projectsData = new MatTableDataSource(this.projects);
    }

    getProjects(update = false) {
        let query: any = {
            limit: 1000,
            archived: true
        }

        this.projectService.searchAndCountProjects(this.searchText, query).pipe(takeUntil(this.destroy$)).subscribe({
            next: (result: { results: Project[], total: number }) => {
                this.projects = result.results;
                this.parseProjects();

                if (update) {
                    this.onGraphTypeChange();
                    this.updateWidget();
                }
            },
            error: err => {
                this.projectLoading = false;
                console.error(err);
            }
        });
    }

    getPlanningData(planning: any) {
        this.planningService.getPlanningData(planning).pipe(take(1)).subscribe({
            next: async (planning) => {
                this.planning = planning.planning;
                this.tasks = planning.tasks.filter((t: any) => t.fragments?.length);
                this.setFields()
            },
            error: (error) => {
                console.error(error)
            }
        });
    }

    getPlanning(planningId: any) {
        this.planningService.getPlanning(planningId).pipe(take(1)).subscribe({
            next: async (planning) => {
                try {
                    this.planning = planning;
                    this.widget.content.planning = this.planning;
                    // this.model.projectXY = threeDTools.getXYFromLatLonAlt2(this.model.origin.position.lat, object.origin.position.lon, 0)
                } catch (e) {
                    console.error("Error: ", e)
                }
            },
            error: (error) => {
                console.error(error)
            }
        });
    }

    getModel(modelId: any) {
        this.modelService.getModel(modelId).pipe(takeUntil(this.destroy$)).subscribe({
            next: async model => {
                this.model = model;
                if (this.widget.content) {
                    this.widget.content.model = this.model;
                }
            },
            error: error => {
                console.error(error);
            }
        });
    }

    getModels() {
        this.modelService.getModels().pipe(takeUntil(this.destroy$)).subscribe({
            next: async models => {
                this.models = models;
            },
            error: error => {
                console.error(error);
            }
        });
    }

    getPlannings() {
        this.planningService.getPlannings().pipe(takeUntil(this.destroy$)).subscribe({
            next: async plannings => {
                this.plannings = plannings;
                this.setFields();
            },
            error: error => {
                console.error(error);
            }
        });
    }

    getZones() {
        this.zoneService.getZones().pipe(takeUntil(this.destroy$)).subscribe({
            next: (zones) => {
                this.zones = zones;
            },
            error: error => console.error(error)
        });
    }

    onProjectSelected() {
        this.updateWidget();
    }

    onDatabaseSeriesChanged() {
        this.setFields();
        this.updateWidget();
        this.setAggregationVisibility("series");
    }

    onLimitChanged() {
        this.updateWidget();
    }

    applyTemplate(template: WidgetV2) {
        this.widget = {
            ...template,
            data: [],
            content: {
                zones: this.zones,
                model: this.model
            },
            refresh: new EventEmitter(),
            version: 0
        }
        delete this.widget.id;
        this.onGraphTypeChange();

        setTimeout(() => {
            this.updateWidget();
        }, 200)
    }

    // Manual series, in widget.dataset (created with its card).
    get manualSeries(): { enable?: boolean; label?: string; data: { x: string; y: string }[] } {
        const dataset = this.widget.dataset ?? (this.widget.dataset = {});
        dataset.data ??= [];
        return dataset;
    }

    addDatasetData() {
        this.manualSeries.data.push({ x: "", y: "" });
    }

    deleteDatasetData(index: number) {
        this.manualSeries.data.splice(index, 1);
        this.updateWidget();
    }

    actionForm(action: any) {
        const functionName = action.name;

        if (typeof (this as any)[functionName] === 'function') {
            (this as any)[functionName]();
        }
    }

    changeOrder() {
        // const dialogRef = this.dialog.open(OrderDataComponent, {
        //     width: '600px',
        //     data: {
        //         data: this.widget,
        //         title: 'Order Data'
        //     }
        // });

        // dialogRef.afterClosed().subscribe(result => {
        //     if (result) {
        //         this.widget.design.yAxis.enableCustomOrder = true;
        //         this.widget.design.yAxis.customOrder = result.map((r: any) => r.x);
        //         this.widget.data = result;
        //         this.updateWidget();
        //     }
        // });
    }

    closeDialog() {
        this.dialogRef.close();
    }

    onCompareMode() {
        this.onGraphTypeChange();
        this.widget.refresh?.emit("data");
    }

    getSelectedGraphType(): any {
        return this.graphTypes.find(type => type.value === this.widget.graphType);
    }

    // ...existing code...

    onQueryUpdated(obj: any) {

        this.query = obj;

    }

    getEmbedMacro() {
        const body = this.query;
        const bodyJson = JSON.stringify(body).replaceAll('"', '""');
        let codeM = `
let
    url = "https://api.cad42.com/charts/aggregate",
    body = "${bodyJson}",

    source = Json.Document(
        Web.Contents(
            url,
            [
                Headers = [
                    #"Content-Type" = "application/json",
                    #"x-project-id" = "${this.project?.id}",
                    #"x-access-key" = "${this.project?.accessKey}"
                ],
                Content = Text.ToBinary(body)
            ]
        )
    ),
        
    #"table" = Table.FromList(source, Splitter.SplitByNothing(), null, null, ExtraValues.Error)
in
    #"table"
    `
        this.tools.copy(codeM)
    }

    getEmbedUrl() {
        if (this.project && this.widget && this.dashboard) {
            this.tools.copy(`http://app.unifield.io/embed/${this.widget.type}?key=${this.project.accessKey}&project=${this.project.id}&chart=${this.widget.id}&dashboard=${this.dashboard.id}`);
            this.pop.toast(this.translate.instant('editor.copy_success'), "success")
        } else {
            this.pop.toast(this.translate.instant('editor.copy_failed'), "warning")
        }
    }
}
